import React, { useState } from 'react';
import { 
  Home, 
  FileText, 
  Users, 
  BarChart3, 
  Zap, 
  Palette, 
  Settings, 
  BookOpen, 
  TestTube, 
  Edit3, 
  ArrowRight, 
  ChevronLeft, 
  ChevronRight,
  Menu,
  X,
  Mic
} from 'lucide-react';
import { ViewType } from '../../types';
import { shouldShowTestingFeatures, shouldShowDebugFeatures, getUserMode, shouldShowFeature } from '../../utils/environment';

interface CollapsibleSidebarProps {
  id?: string;
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({ 
  id,
  currentView, 
  onViewChange, 
  isCollapsed, 
  onToggleCollapse 
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const userMode = getUserMode();
  
  const navigationItems = [
    { 
      id: 'dashboard' as ViewType, 
      label: 'Dashboard', 
      icon: Home,
      description: 'Overview & stats',
      visible: shouldShowFeature('dashboard', userMode)
    },
    { 
      id: 'content' as ViewType, 
      label: 'Content', 
      icon: FileText,
      description: 'Posts, templates, media, RSS & moderation',
      visible: shouldShowFeature('content', userMode)
    },
    { 
      id: 'audience' as ViewType, 
      label: 'Audience', 
      icon: Users,
      description: 'Subscribers & segments',
      visible: shouldShowFeature('audience', userMode)
    },
    { 
      id: 'insights' as ViewType, 
      label: 'Analytics', 
      icon: BarChart3,
      description: 'Analytics & revenue',
      visible: shouldShowFeature('analytics', userMode)
    },
    { 
      id: 'automation' as ViewType, 
      label: 'Automation', 
      icon: Zap,
      description: 'Campaigns & triggers',
      visible: shouldShowFeature('automation', userMode)
    },
    { 
      id: 'brand' as ViewType, 
      label: 'Brand', 
      icon: Palette,
      description: 'Themes & customization',
      visible: shouldShowFeature('brand', userMode)
    },
    { 
      id: 'reader' as ViewType, 
      label: 'Reader', 
      icon: BookOpen,
      description: 'Manage subscriptions & discover content',
      visible: shouldShowFeature('reader', userMode)
    },
    { 
      id: 'reader-test' as ViewType, 
      label: 'Reader Test',
      icon: TestTube,
      description: 'Test reader features',
      visible: shouldShowTestingFeatures() && shouldShowFeature('testing', userMode)
    },
    { 
      id: 'testing' as ViewType, 
      label: 'Testing Suite',
      icon: TestTube,
      description: 'Comprehensive platform testing',
      visible: shouldShowTestingFeatures() && shouldShowFeature('testing', userMode)
    },
    { 
      id: 'media-studio' as ViewType, 
      label: 'Media Studio', 
      icon: Mic,
      description: 'Record, edit & publish audio/video',
      visible: shouldShowFeature('content', userMode)
    },
    { 
      id: 'account' as ViewType, 
      label: 'Account', 
      icon: Settings,
      description: 'Billing & settings',
      visible: shouldShowFeature('account', userMode)
    }
  ].filter(item => item.visible);

  const handleItemClick = (view: ViewType) => {
    onViewChange(view);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
        data-testid="mobile-menu"
      >
        {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        id={id}
        className={`
          fixed lg:relative z-50 lg:z-auto
          ${isCollapsed ? 'w-16' : 'w-64'}
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          transition-all duration-300 ease-in-out
          bg-white dark:bg-gray-800 
          border-r border-gray-200 dark:border-gray-700 
          min-h-screen
          ${isCollapsed ? 'p-2' : 'p-6'}
        `} 
        data-testid="navigation"
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Desktop Toggle Button */}
        <div className="hidden lg:flex justify-end mb-6">
          <button
            onClick={onToggleCollapse}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            )}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="space-y-2">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                  ${isActive
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-l-4 border-blue-500 dark:border-blue-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  }
                  ${isCollapsed ? 'justify-center px-2' : ''}
                `}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && (
                  <div className="flex flex-col items-start min-w-0">
                    <span className="font-medium truncate">{item.label}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.description}</span>
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom Section - Only show when expanded */}
        {!isCollapsed && (
          <div className="mt-8 space-y-4">
            {/* Migration CTA */}
            <div className="p-4 bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg border border-green-100 dark:border-green-800">
              <div className="text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">Migrate from Substack</div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">Seamless migration in minutes</div>
              <a
                href="/migration"
                className="inline-flex items-center text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
              >
                Start Migration
                <ArrowRight className="w-3 h-3 ml-1" />
              </a>
            </div>

            {/* Platform Fee */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
              <div className="text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">Platform Fee</div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">5%</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">vs Substack's 10%</div>
              <div className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium">Save $117/month</div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default CollapsibleSidebar;
